import { defineStore } from 'pinia'
import { transactionsAPI } from '../services/api'

export const useTransactionsStore = defineStore('transactions', {
  state: () => ({
    transactions: [],
    currentTransaction: null,
    matchedRows: [],
    matchSummary: null,
    loading: false,
    error: null,
    filters: {
      status: null,
      match_status: null,
      limit: 50,
      offset: 0
    },
    total: 0
  }),

  getters: {
    pendingTransactions: (state) =>
      state.transactions.filter((t) => t.processing_status === 'itemized' && !t.user_confirmed),

    confirmedTransactions: (state) =>
      state.transactions.filter((t) => t.user_confirmed),

    syncedTransactions: (state) =>
      state.transactions.filter((t) => t.synced_to_monarch_at)
  },

  actions: {
    async fetchTransactions(filters = {}) {
      try {
        this.loading = true
        this.error = null

        const params = { ...this.filters, ...filters }
        const response = await transactionsAPI.getAll(params)

        this.transactions = response.data.transactions
        this.total = response.data.total
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to fetch transactions'
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchTransaction(id) {
      try {
        this.loading = true
        this.error = null

        const response = await transactionsAPI.getById(id)
        this.currentTransaction = response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to fetch transaction'
        throw error
      } finally {
        this.loading = false
      }
    },

    async updateTransaction(id, data) {
      try {
        const response = await transactionsAPI.update(id, data)

        // Update in list
        const index = this.transactions.findIndex((t) => t.id === id)
        if (index !== -1) {
          this.transactions[index] = response.data.transaction
        }

        // Update current if it's the same
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = response.data.transaction
        }

        return response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to update transaction'
        throw error
      }
    },

    async confirmTransaction(id) {
      try {
        const response = await transactionsAPI.confirm(id)

        // Update in list
        const index = this.transactions.findIndex((t) => t.id === id)
        if (index !== -1) {
          this.transactions[index] = response.data.transaction
        }

        // Update current if it's the same
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = response.data.transaction
        }

        return response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to confirm transaction'
        throw error
      }
    },

    async acceptSuggestions(id) {
      try {
        const response = await transactionsAPI.acceptSuggestions(id)

        // Update in list
        const index = this.transactions.findIndex((t) => t.id === id)
        if (index !== -1) {
          this.transactions[index] = response.data.transaction
        }

        // Update current if it's the same
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = response.data.transaction
        }

        return response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to accept AI suggestions'
        throw error
      }
    },

    async resetTransaction(id) {
      try {
        const response = await transactionsAPI.reset(id)

        // Update in list
        const index = this.transactions.findIndex((t) => t.id === id)
        if (index !== -1) {
          this.transactions[index] = response.data.transaction
        }

        // Update current if it's the same
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = response.data.transaction
        }

        return response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to reset transaction'
        throw error
      }
    },

    async reparseTransaction(id) {
      try {
        const response = await transactionsAPI.reparse(id)

        // Update in list
        const index = this.transactions.findIndex((t) => t.id === id)
        if (index !== -1) {
          this.transactions[index] = response.data.transaction
        }

        // Update current if it's the same
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = response.data.transaction
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to re-parse transaction'
        throw error
      }
    },

    async dismissTransaction(id) {
      try {
        const response = await transactionsAPI.dismiss(id)

        // Update in list
        const index = this.transactions.findIndex((t) => t.id === id)
        if (index !== -1) {
          this.transactions[index] = response.data.transaction
        }

        // Update current if it's the same
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = response.data.transaction
        }

        return response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to dismiss transaction'
        throw error
      }
    },

    async deleteTransaction(id) {
      try {
        await transactionsAPI.delete(id)

        // Remove from list
        this.transactions = this.transactions.filter((t) => t.id !== id)

        // Clear current if it's the same
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = null
        }
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to delete transaction'
        throw error
      }
    },

    async updateItem(transactionId, itemId, data) {
      try {
        const response = await transactionsAPI.updateItem(transactionId, itemId, data)

        // Update item in current transaction
        if (this.currentTransaction?.id === transactionId) {
          const itemIndex = this.currentTransaction.items.findIndex((i) => i.id === itemId)
          if (itemIndex !== -1) {
            this.currentTransaction.items[itemIndex] = response.data.item
          }
        }

        return response.data.item
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to update item'
        throw error
      }
    },

    async deleteItem(transactionId, itemId) {
      try {
        await transactionsAPI.deleteItem(transactionId, itemId)

        // Remove item from current transaction
        if (this.currentTransaction?.id === transactionId) {
          this.currentTransaction.items = this.currentTransaction.items.filter(
            (i) => i.id !== itemId
          )
        }
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to delete item'
        throw error
      }
    },

    async fetchMatchedTransactions(params = {}) {
      try {
        this.loading = true
        this.error = null

        const response = await transactionsAPI.getMatched(params)
        this.matchedRows = response.data.rows
        this.matchSummary = response.data.summary
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to fetch matched transactions'
        throw error
      } finally {
        this.loading = false
      }
    },

    async matchTransaction(gmailId, monarchId) {
      try {
        const response = await transactionsAPI.update(gmailId, {
          monarch_transaction_id: monarchId
        })
        return response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to match transaction'
        throw error
      }
    },

    async unmatchTransaction(gmailId) {
      try {
        const response = await transactionsAPI.update(gmailId, {
          monarch_transaction_id: null
        })
        return response.data.transaction
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to unmatch transaction'
        throw error
      }
    },

    async autoMatchTransactions() {
      try {
        const response = await transactionsAPI.autoMatch()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to auto-match transactions'
        throw error
      }
    },

    setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
    }
  }
})
